import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

const LAUNCH_DATE = new Date("2026-05-14T12:00:00-04:00");

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const getTimeLeft = (): TimeLeft => {
  const diff = Math.max(0, LAUNCH_DATE.getTime() - Date.now());
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
};

const TimeUnit = ({ value, label }: { value: number; label: string }) => (
  <div className="flex flex-col items-center">
    <span className="text-lg sm:text-xl font-bold tabular-nums text-primary">
      {String(value).padStart(2, "0")}
    </span>
    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
  </div>
);

export const CountdownBanner = ({ navigate }: { navigate: (path: string) => void }) => {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState(getTimeLeft);

  useEffect(() => {
    const id = setInterval(() => setTimeLeft(getTimeLeft()), 1000);
    return () => clearInterval(id);
  }, []);

  const isLive = timeLeft.days === 0 && timeLeft.hours === 0 && timeLeft.minutes === 0 && timeLeft.seconds === 0;

  return (
    <div className="bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20 border border-primary/30 rounded-xl p-4 mb-4">
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5">
        <div className="flex items-center gap-2">
          <span className="px-2 py-1 rounded-full bg-primary text-primary-foreground text-xs font-bold uppercase">
            {t("home.liveBeta")}
          </span>
          <span className="text-sm font-medium text-foreground">
            {isLive ? "🚀 We're live!" : "🎉 Going live in"}
          </span>
        </div>

        {!isLive && (
          <div className="flex items-center gap-2">
            <TimeUnit value={timeLeft.days} label="days" />
            <span className="text-primary font-bold text-lg">:</span>
            <TimeUnit value={timeLeft.hours} label="hrs" />
            <span className="text-primary font-bold text-lg">:</span>
            <TimeUnit value={timeLeft.minutes} label="min" />
            <span className="text-primary font-bold text-lg">:</span>
            <TimeUnit value={timeLeft.seconds} label="sec" />
          </div>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate("/feedback")}
          className="border-primary/50 hover:bg-primary/10"
        >
          {t("home.giveFeedback")}
        </Button>
      </div>
    </div>
  );
};
